import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useAuth } from './useAuth'

const STORAGE_KEY = 'hikestrong-dashboard-users'

export type UserSessionContextValue = {
  activeUserId: string | null
  savedUserIds: string[]
  setActiveUserId: (userId: string | null) => void
  addUserId: (userId: string) => void
  removeUserId: (userId: string) => void
  clearAll: () => void
}

const UserSessionContext = createContext<UserSessionContextValue | undefined>(undefined)

export const UserSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [activeUserId, setActiveUserIdState] = useState<string | null>(null)
  const [savedUserIds, setSavedUserIds] = useState<string[]>(() => {
    if (typeof window === 'undefined') return []
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        return JSON.parse(stored) as string[]
      } catch (error) {
        console.warn('Failed to parse saved user IDs', error)
        window.localStorage.removeItem(STORAGE_KEY)
      }
    }
    return []
  })

  useEffect(() => {
    if (user?.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: sync active user with auth state
      setActiveUserIdState(user.id)
      setSavedUserIds((prev) => {
        if (prev.includes(user.id)) {
          return prev
        }
        const next = [user.id, ...prev]
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        return next
      })
    }
  }, [user?.id])

  const setActiveUserId = useCallback((userId: string | null) => {
    setActiveUserIdState(userId)
    if (userId) {
      setSavedUserIds((prev) => {
        if (prev.includes(userId)) {
          return prev
        }
        const next = [userId, ...prev]
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        return next
      })
    }
  }, [])

  const addUserId = useCallback((userId: string) => {
    setSavedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev
      }
      const next = [userId, ...prev]
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  const removeUserId = useCallback((userId: string) => {
    setSavedUserIds((prev) => {
      const next = prev.filter((id) => id !== userId)
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      if (activeUserId === userId) {
        setActiveUserIdState(next[0] ?? null)
      }
      return next
    })
  }, [activeUserId])

  const clearAll = useCallback(() => {
    setSavedUserIds([])
    setActiveUserIdState(null)
    window.localStorage.removeItem(STORAGE_KEY)
  }, [])

  const value = useMemo(
    () => ({ activeUserId, savedUserIds, setActiveUserId, addUserId, removeUserId, clearAll }),
    [activeUserId, savedUserIds, setActiveUserId, addUserId, removeUserId, clearAll]
  )

  return <UserSessionContext.Provider value={value}>{children}</UserSessionContext.Provider>
}

export const useUserSession = () => {
  const context = useContext(UserSessionContext)
  if (!context) {
    throw new Error('useUserSession must be used within a UserSessionProvider')
  }
  return context
}
